import { create } from 'zustand';

export type ActivityStatus = 'running' | 'ok' | 'error';

export type ActivityEntry = {
  id: string;            // Anthropic tool_use id (call.id)
  tool: string;
  inputSummary: string;
  status: ActivityStatus;
  error?: string;
  startedAt: number;
  finishedAt?: number;
  durationMs?: number;
};

type ActivityState = {
  pending: ActivityEntry[];                       // running for the in-flight turn
  byMessageId: Record<string, ActivityEntry[]>;   // committed per assistant message id

  startActivity: (init: { id: string; tool: string; inputSummary: string }) => void;
  finishActivity: (id: string, ok: boolean, error?: string) => void;
  commitPendingTo: (messageId: string) => void;
  clearPending: () => void;
};

export const useAgentActivityStore = create<ActivityState>()((set) => ({
  pending: [],
  byMessageId: {},
  startActivity: ({ id, tool, inputSummary }) =>
    set((s) => ({
      pending: [
        ...s.pending,
        {
          id,
          tool,
          inputSummary,
          status: 'running',
          startedAt: Date.now(),
        },
      ],
    })),
  finishActivity: (id, ok, error) =>
    set((s) => ({
      pending: s.pending.map((e) => {
        if (e.id !== id) return e;
        const finishedAt = Date.now();
        return {
          ...e,
          status: ok ? 'ok' : 'error',
          error: ok ? undefined : error,
          finishedAt,
          durationMs: finishedAt - e.startedAt,
        };
      }),
    })),
  commitPendingTo: (messageId) =>
    set((s) => {
      if (s.pending.length === 0) return {};
      return {
        pending: [],
        byMessageId: { ...s.byMessageId, [messageId]: s.pending },
      };
    }),
  clearPending: () => set({ pending: [] }),
}));

// Render a tool input as a single-line summary for the activity row.
// Trim long strings so a 5-line WHERE clause doesn't blow up the panel.
export function summarizeToolInput(input: unknown): string {
  if (input == null) return '';
  if (typeof input === 'string') return clip(input, 120);
  if (typeof input !== 'object') return String(input);
  const obj = input as Record<string, unknown>;
  const parts: string[] = [];
  for (const [k, v] of Object.entries(obj)) {
    if (v == null) continue;
    parts.push(`${k}=${formatValue(v)}`);
    if (parts.join(' ').length > 200) break;
  }
  return clip(parts.join(' '), 200);
}

function formatValue(v: unknown): string {
  if (typeof v === 'string') return JSON.stringify(clip(v, 60));
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  if (Array.isArray(v)) return `[${v.length}]`;
  if (typeof v === 'object') {
    const obj = v as Record<string, unknown>;
    if ('kind' in obj && typeof obj.kind === 'string') {
      // Source — show kind plus the most-identifying field.
      if (obj.kind === 'handle' && typeof obj.id === 'string') return `<${obj.id}>`;
      if (obj.kind === 'feature' && obj.layer_id) return `<feat:${String(obj.layer_id)}/${String(obj.objectid ?? '?')}>`;
      if (obj.kind === 'feature_set' && obj.layer_id) return `<set:${String(obj.layer_id)}>`;
      return `<${obj.kind}>`;
    }
    return '{…}';
  }
  return String(v);
}

function clip(s: string, n: number): string {
  return s.length <= n ? s : `${s.slice(0, n - 1)}…`;
}
